import type { RootState } from '@/rootReducer';
import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { EnvelopeGrey, CommCenterIcon } from '@/assets/images';
import Email from '@/modules/email/scenes/Email';
import {
	dashboardGetSelectBusiness,
	dashboardRemoveListRequest,
	dashboardSelectBusiness,
	dashboardSelectBusinessTab
} from '@/modules/dashboard/dashboardActions';
import './stylesRedesign.scss';
import BusinessesList from '@/components/BusinessesList/businessesList';
import FilterComponent from '@/components/BusinessesList/FilterComponent';
import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';
import type { Business, List } from '@/modules/dashboard/dashboardTypes';
import type * as SearchTypes from '@/modules/search/searchTypes';
import type { DirectEmail } from '@/modules/email/emailTypes';
import { sendAnalytics } from '@/services/analyticsService';
import {
	emailGetEmailThread,
	EmailGetEmailThreadProps
} from '@/modules/email/emailActions';
import ListModal from '../../dashboard/components/ListModal/ListModal';
import { CommunicationCenterBusinessPreview } from '../components/CommunicationCenterBusinessPreview/CommunicationCenterBusinessPreview';

interface Props {
	listChange: (list: string) => void;
	lists: any;
}

const CommunicationCenterRedesign = ({
	listChange,
	lists
}: Props): JSX.Element => {
	const [contactedBusinesses, businessSelected, user] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn([
				'businesses',
				'communicationCenter',
				'data'
			]),
			state.dashboard.getIn(['businesses', 'selected']) ||
				state.dashboard.get('businesses').get('selected'),
			state.user.getIn(['authorize', 'user']) ||
				state.user.get('authorize').get('user')
		],
		shallowEqual
	);

	// Safely convert to JS with null checks
	const businessSelectedJS = businessSelected ? businessSelected.toJS() : {};
	const userJS = user ? user.toJS() : {};

	const dispatch = useDispatch();

	const getEmailThread = (props: EmailGetEmailThreadProps) => {
		dispatch(emailGetEmailThread(props));
	};
	const selectBusiness = (business: Business | null) =>
		dispatch(dashboardSelectBusiness(business));
	const getSelectBusiness = (businessId: string, userEmail: string) =>
		dispatch(dashboardGetSelectBusiness({ businessId, userEmail }));

	const [isEmailModalMounted, setIsEmailModalMounted] =
		useState<boolean>(false);

	const [directEmail, setDirectEmail] = useState<DirectEmail>();
	const [filteredLists, setFilteredLists] = useState<List[]>([]);
	const [, setCheckboxListData] = useState<{
		[key: string]: boolean;
	}>({});
	const [saveToList, setSaveToList] = useState<Business[]>();
	const [removeListConfirm, setRemoveListConfirm] = useState<List>();
	const [savedSearch, setSavedSearch] = useState<SearchTypes.Search>();
	const [editList, setEditList] = useState<List>();
	const [openSaveToListModal, setOpenSaveToListModal] =
		useState<boolean>(false);
	const handleCheckboxListChange = (newCheckboxList: {
		[key: string]: boolean;
	}) => {
		setCheckboxListData(prev => ({
			...prev,
			...newCheckboxList // Merge new selections with previous selections
		}));
	};

	useEffect(() => {
		if (lists && lists.length > 0) {
			setFilteredLists(lists);
		}
	}, [lists]);

	const onEmailModalClose = useCallback(() => {
		if (isEmailModalMounted) {
			setIsEmailModalMounted(false);
		}

		if (directEmail) {
			setDirectEmail(undefined);
		}
	}, [isEmailModalMounted, directEmail]);

	const removeList = (l: List) => dispatch(dashboardRemoveListRequest(l));

	const handleSelectBusiness = (business: Business) => {
		if (
			(businessSelectedJS.businessId &&
				businessSelectedJS.businessId === business.businessId) ||
			(businessSelectedJS._id &&
				businessSelectedJS._id === business._id) ||
			(businessSelectedJS.id && businessSelectedJS.id === business.id)
		) {
			return selectBusiness(null); // Additional unselect logic if needed
		}

		// Extract the correct business ID
		// The actual business ID is in business.businessId or business.id
		// Don't use business._id as that might be the note ID
		const businessId = business.businessId || business.id || '';

		// Call getEmailThread with the correct business ID
		getEmailThread({
			businessId,
			userEmail: userJS.email
		});

		getSelectBusiness(businessId, userJS.email);

		// Set the selected tab to 'emails' so it's selected in the preview
		dispatch(dashboardSelectBusinessTab('emails'));

		sendAnalytics({
			type: 'track',
			userId: userJS.email,
			eventName: businessSelectedJS._id
				? 'Switched to another business in Communication Center'
				: 'Opened a business in Communication Center',
			properties: { businessId }
		});
	};

	const [largeListToSave, setLargeListToSave] = useState<
		Business[] | undefined
	>([]);
	return (
		<div className="communication-center-redesign relative">
			<div className="communication-center-outer-holder">
				<ListModal
					businesses={saveToList}
					saveBusinessToList={setSaveToList}
					removeListConfirm={removeListConfirm}
					setRemoveListConfirm={setRemoveListConfirm}
					removeList={removeList}
					savedSearch={savedSearch}
					editList={editList}
					setEditList={setEditList}
					setSavedSearch={setSavedSearch}
					listChange={listChange}
				/>
				<div className="communication-center-div-redesign">
					<HeaderComponent
						icon={<CommCenterIcon />}
						name="Communication Center"
						description="Manage all your business communications, track emails, follow up on leads, and view recent client activities in one organized place."
						listName="TEST LIST NAME"
						emailCounter={4}
						totalEmails={100}
					/>
					{filteredLists && (
						<FilterComponent
							pageName="communication-center"
							lists={filteredLists}
						/>
					)}

					<div className="communication-center-list relative flex">
						<BusinessesList
							user={userJS}
							openSaveToListModal={openSaveToListModal}
							setOpenSaveToListModal={setOpenSaveToListModal}
							businesses={contactedBusinesses}
							headers={[
								'Checkboxes',
								'Business Name',
								'Contact Information',
								'Contact Method',
								'Lead Status',
								'Last Contacted'
							]}
							pageName="communication-center"
							listChange={listChange}
							lists={filteredLists}
							onCheckboxListChange={handleCheckboxListChange}
							onSelectBusiness={handleSelectBusiness}
							setLargeListToSave={setLargeListToSave}
							largeListToSave={largeListToSave || []}
						/>
						{contactedBusinesses?.length === 0 && (
							<div className="communication-center-empty-div">
								<EnvelopeGrey className="communication-center-empty-icon" />
								<span className="communication-center-empty-text">
									No communication found. Try changing your
									search terms, filters, list or date range.
								</span>
							</div>
						)}

						<CommunicationCenterBusinessPreview />
					</div>

					{isEmailModalMounted && businessSelectedJS && (
						<Email.Modal
							linkedInSharer={() => null}
							onClose={onEmailModalClose}
							show={isEmailModalMounted}
							showPreview={false}
							business={businessSelectedJS}
							directEmail={directEmail}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default CommunicationCenterRedesign;
